<template>
  <div class="h-full w-full flex flex-col">
    <div class="box-border h-full w-full flex-1 overflow-y-auto bg-[#f7f9fc] p-4">
      <div v-for="item in chatMessage" :key="item.created_at ?? ''" class="mt-4">
        <message-text :chat-message-item="item" />
      </div>
      <!-- 创建一个位于最下面的div这样子就能每次都移动到这个位置了 -->
      <div ref="bottomEl" class="h-0 w-0"></div>
    </div>
    <a-spin :spinning="loading" tip="Loading...">
      <div class="dock">
        <div class="toolbar">
          <div class="tools">
            <a-tooltip placement="top">
              <template #title>清空输入</template>
              <button class="iconbtn" @click="clearTextarea">
                <icon-vue class="ic" icon="icon-park-outline:clear" />
              </button>
            </a-tooltip>
            <a-tooltip placement="top">
              <template #title>重置会话</template>
              <button class="iconbtn" @click="reloadChat">
                <icon-vue class="ic" icon="uiw:reload" />
              </button>
            </a-tooltip>
            <a-tooltip placement="top">
              <template #title>添加图片（也可直接粘贴截图）</template>
              <button class="iconbtn" :disabled="loading" @click="pickImage">
                <icon-vue class="ic" icon="lucide:image-plus" />
              </button>
            </a-tooltip>
            <input ref="fileInput" type="file" accept="image/*" multiple style="display: none" @change="onPickFiles" />
          </div>
          <span class="count">{{ textarea.length }}/2000</span>
        </div>
        <div class="editor">
          <div v-if="pendingImages.length" class="img-strip">
            <div v-for="(img, i) in pendingImages" :key="i" class="thumb">
              <img :src="img" alt="" />
              <span class="rm" title="移除" @click="removeImage(i)">×</span>
            </div>
          </div>
          <textarea
            ref="textareaRef"
            v-model="textarea"
            class="ta"
            rows="3"
            :placeholder="placeholder"
            :disabled="loading"
            @keydown="keydown"
            @paste="onPaste"
          ></textarea>
          <div class="sendrow">
            <a-button type="primary" class="sendbtn" :loading="loading" @click="sendMessage">发送</a-button>
          </div>
        </div>
      </div>
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import { chatApi } from '@/api/common/chat';
import ossUtil, { OssCategory } from '@/utils/oss';
import { buildChatMessages, buildPersistMessages } from './requirement-assistant';
import { CTC_ROBOT_TOOLS, MAX_TOOL_ITERATIONS, buildReportCard, executeToolCall, interpretResponse } from './tools';
import { TOOL_REGISTRY } from './tool-registry';
import { Icon as IconVue } from '@iconify/vue';
import { message } from 'ant-design-vue';
import { v4 as uuidv4 } from 'uuid';
import { nextTick, onMounted, ref } from 'vue';
import MessageText from './message-item.vue';
import type { ChatMessageItem } from './types';

// 开场白：先问意图，由机器人在对话中自行路由到查询数据 / 提系统需求
const initMessage = `
##### **你好，我是 CTC-AI-Robot** 🤖
你是想**查询数据**，还是**提系统需求**？告诉我就行，我来帮你完成。💬
`;

const chatMessage = ref<ChatMessageItem[]>([]);
// 当前会话ID：每新开/重置会话生成一次，贯穿本会话所有轮次与工具循环，供后端归组留存对话
const conversationId = ref('');
const textarea = ref('');
const placeholder = ref('说说你的问题或想加的功能(shift+enter换行)....');
const loading = ref(false);
// 窗口滚动到最下面的子el位置
const bottomEl = ref<HTMLElement | null>(null);
const textareaRef = ref<HTMLElement | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
// 待发送图片（已压缩的 data URL）；发送时随消息带走，gpt-4o 可识别
const pendingImages = ref<string[]>([]);
const MAX_IMAGES = 4;
// base64 data URL → 已上传私有桶的 OSS URL；落库时用它替换 base64（buildPersistMessages）。
// 会话内累积（历史轮的图仍在上下文里），随重置会话清空。
const ossUrlByDataUrl = new Map<string, string>();
// 进行中的图片上传任务；发送前 await，尽量让本轮图片的 OSS URL 就绪再落库
const uploadTasks: Promise<void>[] = [];

const pickImage = () => fileInput.value?.click();

const onPickFiles = (e: Event) => {
  const input = e.target as HTMLInputElement;
  if (input.files) {
    addImages(Array.from(input.files));
  }
  input.value = ''; // 允许再次选同一文件
};

// 粘贴截图：从剪贴板抓 image 项
const onPaste = (e: ClipboardEvent) => {
  const items = e.clipboardData?.items;
  if (!items) {
    return;
  }
  const files: File[] = [];
  for (const it of Array.from(items)) {
    if (it.type.startsWith('image/')) {
      const f = it.getAsFile();
      if (f) {
        files.push(f);
      }
    }
  }
  if (files.length) {
    e.preventDefault();
    addImages(files);
  }
};

const addImages = async (files: File[]) => {
  for (const f of files) {
    if (!f.type.startsWith('image/')) {
      continue;
    }
    if (pendingImages.value.length >= MAX_IMAGES) {
      message.warning(`最多添加 ${MAX_IMAGES} 张图片`);
      break;
    }
    let dataUrl = '';
    try {
      dataUrl = await fileToDataUrl(f);
    } catch {
      message.error('图片读取失败');
      continue;
    }
    pendingImages.value.push(dataUrl);
    // 异步上传到私有桶留存（失败不阻塞对话；落库时该图退回 base64 兜底）
    uploadTasks.push(uploadImageForPersist(dataUrl));
  }
};

// 把压缩后的图片上传到私有桶，记录 base64 → OSS URL 映射，供落库时替换
const uploadImageForPersist = async (dataUrl: string) => {
  try {
    const file = dataUrlToFile(dataUrl, 'chat-image.jpg');
    const r = await ossUtil.uploadFile(file, OssCategory.AiRobotChat, 'ctc-private');
    if (r?.url) {
      ossUrlByDataUrl.set(dataUrl, r.url);
    }
  } catch (e) {
    console.warn('chatBot 图片上传 OSS 失败，将退回 base64 留存', e);
  }
};

// data URL → File（用压缩后的 jpeg 字节，与发给网关的一致）
const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const [head, b64] = dataUrl.split(',');
  const mime = head.match(/data:(.*?);/)?.[1] ?? 'image/jpeg';
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    arr[i] = bin.charCodeAt(i);
  }
  return new File([arr], filename, { type: mime });
};

const removeImage = (i: number) => pendingImages.value.splice(i, 1);

// 压缩到最长边 1280 + JPEG 0.85，控制体积与 token
const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1280;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const r = Math.min(MAX / width, MAX / height);
          width = Math.round(width * r);
          height = Math.round(height * r);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('no ctx'));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => reject(new Error('img load fail'));
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error('read fail'));
    reader.readAsDataURL(file);
  });

const textareaFocus = () => {
  nextTick(() => {
    textareaRef.value?.focus();
  });
};

// 聊天的时候滚动条滚动到最下方
const setScroll = () => {
  setTimeout(() => {
    bottomEl.value?.scrollIntoView();
  }, 50);
};

// 清除输入框
const clearTextarea = () => {
  textarea.value = '';
};

// 重置聊天
const reloadChat = () => {
  loading.value = true;
  // 新会话：换一个 conversation_id，之后本会话所有请求都带它，后端按此归组留存
  conversationId.value = uuidv4();
  // 清空上一会话的图片 OSS 映射
  ossUrlByDataUrl.clear();
  uploadTasks.length = 0;
  setTimeout(() => {
    chatMessage.value = [
      {
        message_id: null,
        conversation_id: null,
        content: initMessage,
        sender: 1,
        name: null,
        created_at: new Date().toString(),
        updated_at: null,
      },
    ];
    textarea.value = '';
    pendingImages.value = [];
    loading.value = false;
  }, 500);
};

// 发送信息
const sendMessage = async () => {
  try {
    if (textarea.value.trim().length === 0 && pendingImages.value.length === 0) {
      message.warning('请输入内容或添加图片。');
      return;
    }
    if (textarea.value.length > 2000) {
      message.warning('输入的文本不能超过2000字。');
      return;
    }
    loading.value = true;
    const chatMessageContent: ChatMessageItem = {
      message_id: null,
      conversation_id: null,
      content: textarea.value,
      sender: 0,
      name: null,
      created_at: new Date().toString(),
      updated_at: null,
      images: pendingImages.value.length ? [...pendingImages.value] : undefined,
    };
    // const uuid = getUUID(12);
    const chatMessageResultContent: ChatMessageItem = {
      message_id: null,
      conversation_id: null,
      content: '',
      sender: 1,
      name: null,
      created_at: new Date().toString(),
      updated_at: null,
      isLoading: true,
      lodingId: '',
    };
    chatMessage.value.push(chatMessageContent);
    chatMessage.value.push(chatMessageResultContent);
    pendingImages.value = []; // 已随消息带走，清空待发区
    setScroll();

    // 工具循环：模型可调用只读工具查数据，拿到结果再作答；无工具调用即普通对话
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const messages: any[] = buildChatMessages(chatMessage.value, todayStr);
    // 发送前尽量等本轮图片上传完成，让落库时图片能换成 OSS URL（超时/失败则该图退回 base64）
    if (uploadTasks.length) {
      await Promise.allSettled(uploadTasks);
    }
    let finalContent = '';
    const toolResults: { name: string; data: any }[] = []; // 收集本轮工具结果，用于构建报告卡片
    for (let i = 0; i < MAX_TOOL_ITERATIONS; i++) {
      // 留存用消息：有图才构建（把 base64 换成 OSS URL），随 messages 增长逐轮重算；无图则不传
      const persistMessages = ossUrlByDataUrl.size ? buildPersistMessages(messages, ossUrlByDataUrl) : undefined;
      const res = await chatApi.chat(messages, CTC_ROBOT_TOOLS, conversationId.value, persistMessages);
      if (!res.choices || res.choices.length === 0) {
        finalContent = '请求出错了 :( token数超出限制,请重置聊天';
        break;
      }
      const msg = res.choices[0].message;
      const action = interpretResponse(msg);
      if (action.kind === 'final') {
        finalContent = action.content;
        break;
      }
      // 需要调工具：回显中间态，把 assistant(带 tool_calls) 与各 tool 结果追加进上下文后再问
      setResultContent('正在查询数据…');
      messages.push({
        role: 'assistant',
        content: msg.content ?? '',
        tool_calls: msg.tool_calls,
      });
      for (const call of action.calls) {
        // save_work_item 的 rawText(用户原话)是溯源凭证，模型偶尔漏填 → 用首条用户消息兜底，保证不为空
        if (call.name === 'save_work_item' && !(call.args?.rawText || '').toString().trim()) {
          const firstUser = messages.find((m: any) => m.role === 'user' && (m.content || '').toString().trim());
          if (firstUser) {
            call.args = { ...(call.args || {}), rawText: firstUser.content };
          }
        }
        const result = await executeToolCall(call, TOOL_REGISTRY, { conversationId: conversationId.value });
        messages.push({ role: 'tool', tool_call_id: call.id, content: result });
        toolResults.push({ name: call.name, data: result });
      }
      if (i === MAX_TOOL_ITERATIONS - 1) {
        finalContent = '查询步骤过多，已停止。请缩小问题范围后重试。';
      }
    }
    loading.value = false;
    setResultContent(finalContent);
    // 从工具结果构建结构化报告卡片（多段/含分布才出），挂到机器人消息上
    chatMessage.value[chatMessage.value.length - 1].reportCard = buildReportCard(toolResults);
    textarea.value = '';
    textareaFocus();
    setScroll();
  } catch (error) {
    loading.value = false;
    reloadChat();
    message.error('请求超时了 :( 已为您重置聊天');
  }
};

// 设置返回的信息
const setResultContent = (message: string) => {
  chatMessage.value[chatMessage.value.length - 1].content = message;
};

// 设置textarea的keydown事件
const keydown = (event: any) => {
  if (event.shiftKey && event.keyCode === 13) {
    // 获取光标位置并且赋值
    const startPos = event.target.selectionStart;
    const endPos = event.target.selectionEnd;
    const value = event.target.value;
    event.target.value = value.substring(0, startPos) + '\n' + value.substring(endPos);
    event.target.selectionStart = event.target.selectionEnd = startPos + 1;
    textarea.value = value.substring(0, startPos) + '\n' + value.substring(endPos);
    event.preventDefault();
  } else if (event.keyCode === 13) {
    // 回车事件
    sendMessage();
    event.preventDefault();
  }
};

onMounted(() => {
  reloadChat();
});
</script>

<style scoped>
.dock {
  background: #fff;
  border-top: 1px solid #eef0f3;
}
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px 0;
}
.tools {
  display: flex;
  gap: 2px;
}
.iconbtn {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  border: 0;
  background: transparent;
  color: #6b7280;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition:
    background 0.12s,
    color 0.12s;
}
.iconbtn:hover {
  background: #f0f2f5;
  color: #1f2937;
}
.iconbtn .ic {
  width: 16px;
  height: 16px;
}
.count {
  font-size: 11px;
  color: #9aa3af;
  font-variant-numeric: tabular-nums;
}
.editor {
  padding: 6px 12px 12px;
}
.img-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.thumb {
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #eef0f3;
}
.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.thumb .rm {
  position: absolute;
  top: 0;
  right: 0;
  width: 16px;
  height: 16px;
  line-height: 14px;
  text-align: center;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  border-bottom-left-radius: 6px;
}
.ta {
  width: 100%;
  border: 1px solid #eef0f3;
  border-radius: 10px;
  resize: none;
  padding: 9px 11px;
  font-family: inherit;
  font-size: 13.5px;
  line-height: 1.5;
  color: #1f2937;
  outline: none;
  height: 66px;
  background: #fbfcfe;
  transition:
    border-color 0.12s,
    box-shadow 0.12s;
}
.ta:focus {
  border-color: #1677ff;
  box-shadow: 0 0 0 3px rgba(22, 119, 255, 0.12);
}
.ta::placeholder {
  color: #9aa3af;
}
.sendrow {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}
.sendbtn {
  border-radius: 9px;
  font-weight: 600;
}
</style>
